/**
 * What the development chat knows about the bar it is working for (#72).
 *
 * Hayden's bar for the chat: "the AI must understand bars style and recipes
 * formats, suggest real options." Three things stand behind that, and each
 * is a function here that turns the bar's own records into prompt text.
 *
 *   - **How this bar writes a drink.** Every build in the library, in the
 *     units it was written in (`libraryBuilds`), and the conventions they
 *     share — which glass for which method, how much dilution, the garnish
 *     vocabulary, the size of a serve (`houseConventions`). Derived, so it is
 *     never out of date; and an owner's written house-style note, kept on the
 *     organisation's settings, outranks it where they disagree.
 *   - **What is behind the bar tonight** (`stockOnHand`): the venue's counted
 *     levels, so a suggestion uses what is held rather than what is merely
 *     on the ingredient list.
 *   - **What the menu is** (`menuNow`): the drinks on at this venue, with
 *     their prices and GP, so a new one fits a price point and a gap.
 *
 * Pure text in, text out, and no model call: what the chat is told is a
 * thing a test can read.
 */
import { grossProfit } from "./calc.js";
import { specTotals } from "./library.js";
/** A line as the bar writes it: `30 mL Campari`, `2 dash Angostura`, `top Soda`. */
export function lineText(line, ingredients) {
    const name = ingredients.get(line.ingredientId)?.name ?? line.ingredientId;
    const n = (v) => +v.toFixed(1);
    switch (line.unit) {
        case "dash":
        case "drop":
            return `${n(line.qty ?? 1)} ${line.unit} ${name}`;
        case "top":
            return `top ${name}`;
        case "each":
            return `${n(line.qty ?? 1)} × ${name}`;
        case "gram":
            return `${n(line.qty ?? line.ml)} g ${name}`;
        default:
            return `${n(line.ml)} mL ${name}`;
    }
}
/**
 * Every live drink, one per line, with its serve and its build as written.
 * Retired drinks are left out: they are not how the bar writes now.
 */
export function libraryBuilds(specs, ingredients) {
    return [...specs]
        .filter((s) => s.status !== "retired")
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((s) => {
        const serve = [s.style.join("/"), s.method, s.glass, s.ice && `${s.ice} ice`, s.garnish && `garnish ${s.garnish}`]
            .filter(Boolean)
            .join(" · ");
        const build = s.lines.map((l) => lineText(l, ingredients) + (l.batched ? " (batched)" : "")).join(", ") || "no build on file";
        // The batch a drink is poured from, so the chat can see what this bar batches.
        const batch = s.batch.trim() ? `; batch: ${s.batch.trim()}` : "";
        return `${s.name} (${s.specId}, ${s.status}) [${serve || "serve not recorded"}]: ${build}${batch}`;
    })
        .join("\n");
}
function median(values) {
    const sorted = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
    if (sorted.length === 0)
        return null;
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}
/** The commonest values, as `Rocks (12), Coupe (5)`. Case-folded, first spelling kept. */
function commonest(values, limit = 4) {
    const counts = new Map();
    for (const raw of values) {
        const label = raw.trim();
        if (!label)
            continue;
        const key = label.toLowerCase();
        const seen = counts.get(key);
        if (seen)
            seen.n += 1;
        else
            counts.set(key, { label, n: 1 });
    }
    return [...counts.values()]
        .sort((a, b) => b.n - a.n || a.label.localeCompare(b.label))
        .slice(0, limit)
        .map((c) => `${c.label} (${c.n})`)
        .join(", ");
}
/**
 * The conventions the library shares, as short plain sentences. Only from
 * drinks that are on or being tested — a retired drink is not a convention —
 * and only what the library actually records: a convention with no evidence
 * behind it is left unsaid rather than guessed.
 */
export function houseConventions(specs, ingredients) {
    const live = specs.filter((s) => s.status !== "retired" && s.lines.length > 0);
    if (live.length === 0)
        return [];
    const out = [`${live.length} drinks with a build on file.`];
    const methods = new Map();
    for (const s of live) {
        const m = s.method.trim().toLowerCase();
        if (!m)
            continue;
        methods.set(m, [...(methods.get(m) ?? []), s]);
    }
    for (const group of [...methods.values()].sort((a, b) => b.length - a.length).slice(0, 4)) {
        const label = group[0].method.trim();
        const glass = commonest(group.map((s) => s.glass), 3);
        const ice = commonest(group.map((s) => s.ice), 3);
        const dilution = median(group
            .map((s) => s.lines.filter((l) => ingredients.get(l.ingredientId)?.kind === "dilution").reduce((t, l) => t + l.ml, 0))
            .filter((ml) => ml > 0));
        const parts = [
            glass && `glass ${glass}`,
            ice && `ice ${ice}`,
            dilution !== null && `dilution about ${Math.round(dilution)} mL`,
        ].filter(Boolean);
        if (parts.length > 0)
            out.push(`${label} (${group.length}): ${parts.join("; ")}.`);
    }
    const totals = live.map((s) => specTotals(s, ingredients));
    const volume = median(totals.map((t) => t.volumeMl));
    const abv = median(totals.map((t) => t.abv));
    const lines = median(live.map((s) => s.lines.length));
    if (volume !== null && abv !== null && lines !== null) {
        out.push(`A typical serve is about ${Math.round(volume)} mL in the glass at ${abv.toFixed(1)}% ABV, from ${Math.round(lines)} lines.`);
    }
    const garnishes = commonest(live.map((s) => s.garnish), 8);
    if (garnishes)
        out.push(`Garnishes as the bar writes them: ${garnishes}.`);
    const units = new Set(live.flatMap((s) => s.lines.map((l) => l.unit)).filter((u) => u !== "ml"));
    if (units.size > 0)
        out.push(`Besides millilitres the book writes ${[...units].join(", ")} where a measure is not poured.`);
    const spirits = commonest(live.flatMap((s) => s.lines
        .filter((l) => ingredients.get(l.ingredientId)?.kind === "spirit")
        .map((l) => ingredients.get(l.ingredientId).name)), 6);
    if (spirits)
        out.push(`Spirits the book reaches for most: ${spirits}.`);
    return out;
}
/**
 * What is held at the venue, from its stock levels. Ingredients with no
 * level are not "out" — nobody has counted them — so they are named apart,
 * and the chat is told the difference.
 */
export function stockOnHand(levels, ingredients) {
    const counted = levels.filter((l) => ingredients.has(l.ingredientId));
    if (counted.length === 0)
        return { counted: false, text: "" };
    const name = (id) => ingredients.get(id)?.name ?? id;
    const held = counted.filter((l) => l.heldMl > 0).sort((a, b) => name(a.ingredientId).localeCompare(name(b.ingredientId)));
    const out = counted.filter((l) => !(l.heldMl > 0)).map((l) => name(l.ingredientId)).sort();
    const lastCount = counted.reduce((max, l) => (l.updatedAt > max ? l.updatedAt : max), "");
    const lines = [
        `Last counted ${lastCount.slice(0, 10)}.`,
        `Held: ${held.map((l) => `${name(l.ingredientId)} ${Math.round(l.heldMl)} mL`).join(", ") || "nothing"}.`,
    ];
    if (out.length > 0)
        lines.push(`Counted at none: ${out.join(", ")}.`);
    return { counted: true, text: lines.join("\n") };
}
/**
 * The drinks on at the venue now, with price and GP, and the menu's price
 * band — so a new drink is pitched at a price the list already charges.
 */
export function menuNow(specs, overrides, ingredients) {
    const on = specs
        .filter((s) => s.status === "available" && overrides.get(s.specId)?.available !== false)
        .map((s) => {
        const price = overrides.get(s.specId)?.price ?? null;
        const cost = specTotals(s, ingredients).cost;
        return { s, price, gp: price ? grossProfit(price, cost) : null };
    })
        .sort((a, b) => a.s.name.localeCompare(b.s.name));
    if (on.length === 0)
        return "";
    const prices = on.map((d) => d.price).filter((p) => p !== null);
    const gps = on.map((d) => d.gp).filter((g) => g !== null);
    const band = prices.length > 0
        ? `Prices run $${Math.min(...prices).toFixed(0)}–$${Math.max(...prices).toFixed(0)}, most around $${(median(prices) ?? 0).toFixed(0)}` +
            (gps.length > 0 ? `; median GP ${(median(gps) ?? 0).toFixed(1)}%.` : ".")
        : "No venue prices are set.";
    const list = on
        .map((d) => `${d.s.name}${d.price ? ` $${d.price.toFixed(2)}` : ""}${d.gp !== null ? ` (${d.gp.toFixed(0)}% GP)` : ""}`)
        .join(", ");
    return `${on.length} drinks on. ${band}\n${list}`;
}
//# sourceMappingURL=house.js.map